/**
 * Reveal — V109-5-2 (PRD G-8, PD-6; design run `v109-track5-views` round 3, Scope `?view=inventory`;
 * built-UI fix pass). Everything the latest sweep found, not a summary: every resource grouped by
 * provider, with its environment, account or project, its disposition for THIS program first (another
 * program's differing one shown beside it, never merged — D-103-14), where it was found, and the clues
 * still to confirm pinned on top. Unresolved and not-yet-evaluated things read as such, never as in.
 *
 *   - Freshness: "Swept today 06:02 · vs yesterday 06:01", Scope behind a disclosure, Sweep again.
 *   - "10 found · 2 clues to confirm" — the same words as the notice, the toast and Setup.
 *   - Filter chips are the disposition counts (they add up to All; `aria-pressed`); "Review N clues"
 *     sets the Unresolved filter and focuses the first Confirm.
 *   - A clue: "Confirm it's used" opens an inline form (the provider from GAC Pro's catalog, pre-filled
 *     from what was found; Esc / Cancel hand focus back), "Not ours" dismisses it. A provider not in the
 *     catalog: "Other (not listed)…" saves an External service with the provider pending and asks GAC Pro
 *     to add it (BU-4; listed under "Provider pending · N", never "Not ours"). Either shows
 *     "<name> marked not ours · Undo" for 15 seconds (announced); "Not ours · N" lists the dismissed
 *     ones with Restore. After a clue goes, focus moves to the next clue's Confirm, else the heading.
 *   - "Change" opens the resource's disposition in the boundary editor (its component's switch, matched
 *     by asset, discovered resource id, then name — BU-1); "Add to boundary" opens Add component with
 *     its name when no component stands for it. Someone who can't change it gets "Ask an admin" instead.
 *   - At ≤720px each row reads as a name line and one muted line (explicit table roles kept).
 *
 * Reads (any member): GET /sweeps/summary, /resources, /resources/clues?status=open|dismissed, the
 * program's boundary components. Writes (owner / admin; server-enforced): resolve / dismiss / reopen a
 * clue. Others get one "Ask an admin" path.
 * `program` null = the workspace-wide inventory (every workspace reaches it, PD-6).
 */
import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, Select, Spinner } from '@aigovops/design-system';
import { INFRA_RESOURCE_TYPE_SPECS, PROVIDERS, PROVIDER_PENDING_PREFIX, getProvider, isInfraResourceType, isProviderPending, nativeIdOf, type BoundaryComponent, type InfraResource, type UnresolvedClue } from '@aigovops/core';
import { dismissClue, getSweepSummary, listDismissedClues, listOpenClues, listSweepResources, reopenClue, resolveClue, resolveClueUnlisted, sweepKeys } from '../../lib/sweep-api';
import { listComponents } from '../../lib/boundary-api';
import { clockText, clueWhere, DISPOSITION_TEXT, filterReveal, foundText, REVEAL_FILTERS, revealModel, type RevealDisposition, type RevealRow } from '../../lib/sweep-model';
import { programLabel } from '../../lib/boundary-model';
import { useAnnounce } from '../today/shared';
import { AskAdmin } from '../AskAdmin';
import { SweepFreshness } from './SweepFreshness';
import './sweep.css';

const ENV_TEXT: Record<string, string> = { production: 'Production', staging: 'Staging', development: 'Development', other: 'Other' };
const typeLabel = (t: string) => (INFRA_RESOURCE_TYPE_SPECS as Record<string, { label: string }>)[t]?.label ?? t;
const SOURCE_WORD: Record<string, string> = {
  repo_scan: 'Code', connector_scope: 'A connection', gcp_asset_inventory: 'Google Cloud', cloudflare_collector: 'Cloudflare', manual: 'Added by hand',
};
/** Where it was found, in words ("Google Cloud + code"); "Added by hand" when only a person added it. */
const foundIn = (r: InfraResource) => {
  if (r.origin_label) return r.origin_label;
  const names = [...new Set(r.sources.filter((s) => !s.gone_at).map((s) => (s.source_kind === 'connector_scope' ? (r.item ? getProvider(r.item.provider_key)?.name : null) ?? SOURCE_WORD.connector_scope : SOURCE_WORD[s.source_kind] ?? 'A connection')))];
  return names.join(' + ') || '—';
};

/** The provider name a pending clue was saved under ("Provider pending: Mixpanel · external service …"). */
const pendingName = (c: UnresolvedClue) => (c.dismiss_reason ?? '').slice(PROVIDER_PENDING_PREFIX.length).split(' · ')[0] || c.raw_identifier;

/** How long "… · Undo" stays (at least 10 s, built-UI #6). */
export const UNDO_MS = 15_000;

/** The catalog provider a clue most likely is (its key, name or one of its domain / package aliases), else ''. */
export function guessProvider(raw: string): string {
  const s = raw.toLowerCase();
  for (const p of PROVIDERS) {
    if (s === p.key || s.split(/[^a-z0-9]+/).includes(p.key)) return p.key;
    if (p.aliases.some((a) => (a.kind === 'domain' && (s === a.value || s.endsWith(`.${a.value}`))) || (a.kind === 'sdk' && a.value.split(':').pop() === s))) return p.key;
  }
  return '';
}

type ComponentRef = Pick<BoundaryComponent, 'component_id' | 'component_key' | 'asset_id'> & { source_resource_id?: string | null; infra_item_id?: string | null };

/** The provider-native id inside a resource key (`gcp:projects/<id>` → `<id>`); the last path segment for a type core doesn't know. */
function nativeOf(r: Pick<InfraResource, 'resource_type' | 'resource_key'>): string {
  const known = isInfraResourceType(r.resource_type) ? nativeIdOf(r.resource_type, r.resource_key) : null;
  return known ?? r.resource_key.slice(r.resource_key.lastIndexOf('/') + 1);
}

/**
 * BU-1: the boundary component that stands for a resource, in order — the linked asset; the
 * resource it was discovered from (`source_resource_id` = the resource key or its native id); a
 * component named for it (its name or native id); the one component on its inventory item. Null when
 * truly none stands for it.
 */
export function componentFor(r: Pick<InfraResource, 'asset_id' | 'resource_key' | 'resource_type' | 'name' | 'item_id'>, components: readonly ComponentRef[]): ComponentRef | null {
  const lc = (s: string) => s.trim().toLowerCase();
  if (r.asset_id) { const c = components.find((x) => x.asset_id === r.asset_id); if (c) return c; }
  const native = nativeOf(r);
  const keys = new Set([r.resource_key, native].map(lc));
  const bySource = components.find((x) => x.source_resource_id && keys.has(lc(x.source_resource_id)));
  if (bySource) return bySource;
  const names = new Set([r.name, native].filter(Boolean).map(lc));
  const byName = components.find((x) => names.has(lc(x.component_key)));
  if (byName) return byName;
  const onItem = components.filter((x) => x.infra_item_id && x.infra_item_id === r.item_id);
  return onItem.length === 1 ? onItem[0]! : null;
}

/** The boundary editor link for a resource: its component's switch ("Change"), else Add component with its name ("Add to boundary"). */
export function changeLink(boundaryHref: string, r: InfraResource, components: readonly ComponentRef[]): { href: string; label: 'Change' | 'Add to boundary' } {
  const c = componentFor(r, components);
  const q = new URLSearchParams({ ...(c ? { component: c.component_id } : { add: r.name }), edit: 'disposition' });
  return { href: `${boundaryHref}?${q.toString()}`, label: c ? 'Change' : 'Add to boundary' };
}
export const changeHref = (boundaryHref: string, r: InfraResource, components: readonly ComponentRef[]): string => changeLink(boundaryHref, r, components).href;

export function ResourceReveal({ slug, prefix, program, canManage, boundaryHref }: {
  slug: string; prefix: string; program: string | null; canManage: boolean;
  /** Where "Change" opens: this program's boundary editor (or the Boundary page). */
  boundaryHref: string;
}) {
  const qc = useQueryClient();
  const [live, announce] = useAnnounce();
  const summary = useQuery({ queryKey: sweepKeys.summary(slug), queryFn: () => getSweepSummary(slug), staleTime: 30_000, retry: false });
  const resources = useQuery({ queryKey: sweepKeys.resources(slug), queryFn: () => listSweepResources(slug), retry: false });
  const clues = useQuery({ queryKey: sweepKeys.clues(slug), queryFn: () => listOpenClues(slug), retry: false });
  const dismissed = useQuery({ queryKey: sweepKeys.dismissed(slug), queryFn: () => listDismissedClues(slug), retry: false });
  const components = useQuery({ queryKey: ['boundary', slug, 'components'], queryFn: () => listComponents(slug, program!), enabled: !!program, retry: false });
  // `?show=unresolved` (the notice's "N clues to confirm") opens on that filter.
  const [sp] = useSearchParams();
  const [filter, setFilter] = useState<RevealDisposition | null>(() => {
    const s = sp.get('show');
    return REVEAL_FILTERS.some(([k]) => k === s) ? (s as RevealDisposition) : null;
  });
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  /** BU-15: `at` = where the decided clue stood among the shown clues, so Undo sits in its place (next to the next focus). */
  const [undo, setUndo] = useState<{ clue: UnresolvedClue; text: string; at: number } | null>(null);
  const headRef = useRef<HTMLHeadingElement>(null);
  const focusNext = useRef<{ after: string } | null>(null);
  const label = program ? programLabel(program) : null;
  const canWrite = canManage && (resources.data?.can_write ?? false);

  const model = useMemo(() => revealModel(resources.data?.resources ?? [], clues.data ?? [], program, summary.data?.compared_with?.finished_at ?? null),
    [resources.data, clues.data, program, summary.data]);
  const shown = filterReveal(model, filter);

  // After a clue goes: the next clue's Confirm, else the section heading (round 2 #14).
  useEffect(() => {
    if (!focusNext.current || clues.isFetching) return;
    const ids = model.clues.map((c) => c.clue_id);
    focusNext.current = null;
    const next = ids[0];
    const el = next ? document.querySelector<HTMLElement>(`[data-clue="${next}"] [data-part="confirm"]`) : null;
    if (el) el.focus();
    else {
      if (filter === 'unresolved' && !model.counts.unresolved) { setFilter(null); announce(`No clues left · showing all ${model.counts.all}`); }
      headRef.current?.focus();
    }
  }, [model, clues.isFetching, filter, announce]);
  // The Undo line stays for UNDO_MS.
  useEffect(() => {
    if (!undo) return;
    const t = window.setTimeout(() => setUndo(null), UNDO_MS);
    return () => window.clearTimeout(t);
  }, [undo]);

  if (resources.isLoading || clues.isLoading) return <Spinner label="Loading everything the sweep found" />;
  if (resources.error || !resources.data) {
    return <Alert variant="error"><p>The inventory could not load.</p><Button onClick={() => { void resources.refetch(); void clues.refetch(); }}>Retry</Button></Alert>;
  }

  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: sweepKeys.all(slug) })]);
  const reviewClues = () => {
    setFilter('unresolved');
    announce(`Showing ${model.counts.unresolved} unresolved`);
    window.setTimeout(() => document.querySelector<HTMLElement>('[data-clue] [data-part="confirm"]')?.focus(), 0);
  };
  const pick = (f: RevealDisposition | null) => {
    setFilter(f); setOpenRow(null); setConfirming(null);
    const n = filterReveal(model, f).shown;
    announce(`${n} shown`);
  };
  const reopen = async (c: UnresolvedClue, how: 'undo' | 'restore') => {
    try {
      await reopenClue(slug, c.clue_id);
      setUndo(null);
      await refresh();
      announce(`${c.raw_identifier} is back to confirm.`);
      window.setTimeout(() => document.querySelector<HTMLElement>(`[data-clue="${c.clue_id}"] [data-part="confirm"]`)?.focus(), 50);
    } catch (e) { announce(e instanceof Error ? e.message : how === 'undo' ? 'Undo did not work.' : 'It could not be restored.'); }
  };
  const byProvider = model.groups.map((g) => `${g.rows.length} ${g.label}`);
  const newCount = model.groups.reduce((s, g) => s + g.rows.filter((r) => r.isNew).length, 0);
  const liveResources = model.counts.all - model.clues.length;
  // BU-4: a clue confirmed with its provider pending is used — never listed as "Not ours".
  const notOurs = (dismissed.data ?? []).filter((c) => !isProviderPending(c));
  const pending = (dismissed.data ?? []).filter(isProviderPending);
  const undoLine = undo ? (
    <p className="sw-undo" data-part="clue-undo">
      <span>{undo.text}</span>
      <button type="button" className="sw-textbtn" onClick={() => void reopen(undo.clue, 'undo')}>Undo<span className="sr-only">{`: ${undo.clue.raw_identifier}`}</span></button>
    </p>
  ) : null;
  // BU-15: in the table, the Undo line takes the decided clue's row (a full-width row in the clues group).
  const undoRow = undo ? <div role="row" className="sw-undo-row"><span role="cell" aria-colspan={6}>{undoLine}</span></div> : null;
  const undoAt = undo ? Math.min(undo.at, shown.clues.length) : -1;

  return (
    <section className="sw-view" aria-labelledby="rv-h" data-part="reveal" data-reconcile={model.reconciles ? 'ok' : 'filters ≠ all'}>
      {live}
      <SweepFreshness slug={slug} prefix={prefix} run={summary.data?.run} compared={summary.data?.compared_with} sources={summary.data?.sources ?? []} canSweep={canManage} announce={announce} id="rv-fresh" />

      {model.clues.length ? (
        <div className="sw-band" data-tone="warn" data-part="clue-band">
          <span className="sw-band-main"><b><span aria-hidden="true">⚠ </span>{`${model.clues.length} clue${model.clues.length === 1 ? '' : 's'} to confirm`}</b>
            <span className="sw-dim">Found in your code; not counted until you confirm</span></span>
          <Button variant="primary" onClick={reviewClues}>{`Review ${model.clues.length} clue${model.clues.length === 1 ? '' : 's'}`}</Button>
        </div>
      ) : null}

      {undo && shown.shown === 0 ? undoLine : null}

      <div className="sw-head">
        <h3 id="rv-h" ref={headRef} tabIndex={-1} className="gp-sec-h">{foundText(liveResources, model.clues.length)}</h3>
        <span className="sw-dim sw-head-meta">
          {byProvider.map((t, i) => <span key={t}>{i ? <span aria-hidden="true">{' · '}</span> : null}{t}</span>)}
          {newCount ? <span><span aria-hidden="true">{' · '}</span><span className="sw-new">New</span>{` ${newCount} since ${clockText(summary.data?.compared_with?.finished_at, new Date())}`}</span> : null}
        </span>
      </div>
      {label ? <p className="sw-dim sw-note">{`Disposition · ${label} first`}</p> : null}

      <div className="sw-filters" role="group" aria-label="Show">
        <button type="button" className="sw-filter" aria-pressed={filter === null} onClick={() => pick(null)}>All <b>{model.counts.all}</b></button>
        {REVEAL_FILTERS.map(([k, l]) => (
          <button key={k} type="button" className="sw-filter" aria-pressed={filter === k} onClick={() => pick(k)}>{l} <b>{model.counts[k]}</b></button>
        ))}
      </div>

      {shown.shown === 0 ? (
        model.counts.all === 0
          ? <div className="sw-empty"><p>Nothing found yet.</p><Link className="gp-linkbtn" to={`${prefix}/setup`}>Connect a cloud account</Link></div>
          : <div className="sw-empty"><p>Nothing here with this filter.</p><button type="button" className="sw-textbtn" onClick={() => pick(null)}>{`Show all ${model.counts.all}`}</button></div>
      ) : (
        <div className="sw-table" role="table" aria-labelledby="rv-h">
          <div role="rowgroup" className="sw-thead">
            <div role="row" className="sw-tr">
              <span role="columnheader">Name</span><span role="columnheader">Environment</span><span role="columnheader">Account or project</span>
              <span role="columnheader">{label ? `Disposition · ${label}` : 'Disposition'}</span><span role="columnheader">Found in</span><span role="columnheader"><span className="sr-only">Action</span></span>
            </div>
          </div>
          {shown.clues.length || undo ? (
            <div role="rowgroup" className="sw-group">
              <div role="row" className="sw-group-h"><span role="rowheader" aria-colspan={6}>{`Clues to confirm · ${shown.clues.length}`}</span></div>
              {shown.clues.map((c, i) => (
                <Fragment key={c.clue_id}>
                  {i === undoAt ? undoRow : null}
                  <ClueRow c={c} slug={slug} canWrite={canWrite} open={confirming === c.clue_id} onOpen={(o) => setConfirming(o ? c.clue_id : null)}
                    onDone={async (text, decided) => {
                      setConfirming(null); focusNext.current = { after: c.clue_id }; await refresh();
                      setUndo({ clue: decided, text, at: i }); announce(`${text}. Undo is available.`);
                    }} />
                </Fragment>
              ))}
              {undoAt === shown.clues.length ? undoRow : null}
            </div>
          ) : null}
          {shown.groups.map((g) => (
            <div role="rowgroup" key={g.key} className="sw-group">
              <div role="row" className="sw-group-h"><span role="rowheader" aria-colspan={6}>{`${g.label} · ${g.rows.length}`}</span></div>
              {g.rows.map((r) => <ResourceRow key={r.resource.resource_id} row={r} label={label} open={openRow === r.resource.resource_id} slug={slug} canChange={canManage}
                onToggle={() => setOpenRow(openRow === r.resource.resource_id ? null : r.resource.resource_id)}
                change={program && components.data ? changeLink(boundaryHref, r.resource, components.data.components) : { href: boundaryHref, label: 'Change' }} />)}
            </div>
          ))}
        </div>
      )}
      {notOurs.length ? (
        <details className="sw-notours" data-part="not-ours">
          <summary>{`Not ours · ${notOurs.length}`}</summary>
          <ul>
            {notOurs.map((c) => (
              <li key={c.clue_id}><span><b>{c.raw_identifier}</b><span className="sw-dim">{` · ${clueWhere(c)}`}</span></span>
                {canWrite ? <button type="button" className="sw-textbtn" onClick={() => void reopen(c, 'restore')}>Restore<span className="sr-only">{`: ${c.raw_identifier}`}</span></button> : null}</li>
            ))}
          </ul>
        </details>
      ) : null}
      {pending.length ? (
        <details className="sw-notours" data-part="provider-pending">
          <summary>{`Provider pending · ${pending.length}`}</summary>
          <ul>
            {pending.map((c) => (
              <li key={c.clue_id}><span><b>{pendingName(c)}</b><span className="sw-dim">{` · External service · GAC Pro asked to add it · from ${c.raw_identifier}`}</span></span>
                {canWrite ? <button type="button" className="sw-textbtn" onClick={() => void reopen(c, 'restore')}>Restore<span className="sr-only">{`: ${c.raw_identifier}`}</span></button> : null}</li>
            ))}
          </ul>
        </details>
      ) : null}
      <p className="sw-dim sw-foot">Missing something? <Link to={`${prefix}/setup`}>Connect another account</Link></p>
      {!canWrite && model.clues.length ? <AskAdmin slug={slug} compact verb="confirm clues" what="decides what the product uses" subject="GAC Pro: confirm the clues the sweep found" /> : null}
    </section>
  );
}

function ResourceRow({ row, label, open, onToggle, change, canChange, slug }: { row: RevealRow; label: string | null; open: boolean; onToggle: () => void; change: { href: string; label: string }; canChange: boolean; slug: string }) {
  const r = row.resource;
  const id = `rv-d-${r.resource_id}`;
  const d = row.disposition;
  return (
    <>
      <div role="row" className="sw-tr" data-disposition={d}>
        <span role="cell" className="sw-name">
          <span className="sw-name-line">
            <button type="button" className="sw-disclose" aria-expanded={open} aria-controls={id} onClick={onToggle}>
              <span aria-hidden="true">{open ? '▾' : '▸'}</span><b>{r.name}</b><span className="sr-only">{open ? ': hide details' : ': show details'}</span>
            </button>
            {row.isNew ? <span className="sw-new">New</span> : null}
          </span>
          <span className="sw-sub">{typeLabel(r.resource_type)}</span>
        </span>
        <span role="cell" data-label="Environment">{r.environment ? ENV_TEXT[r.environment] ?? r.environment : 'Environment not known'}</span>
        <span role="cell" data-label="Account or project">{r.account_ref ?? '—'}</span>
        <span role="cell" data-label="Disposition" className="sw-disp">
          {row.others.length ? <span className="sw-differs"><span aria-hidden="true">⚠ </span>Differs</span> : null}
          <DispositionText d={d} />{row.others.length ? <span className="sw-sub">{label ? ` ${label}` : ''}</span> : <span className="sw-sub">{label ? ' · both programs' : ''}</span>}
          {row.others.map((o) => <span key={o.program} className="sw-sub sw-other">{`${programLabel(o.program)}: ${DISPOSITION_TEXT[o.disposition]}`}</span>)}
        </span>
        <span role="cell" data-label="Found in">{foundIn(r)}</span>
        <span role="cell" className="sw-act">
          {canChange
            ? <Link className="sw-textbtn" to={change.href} data-part="change">{change.label}<span className="sr-only">{`: ${r.name}`}</span></Link>
            : <AskAdmin slug={slug} compact verb={`change ${r.name}`} what="changes the boundary" subject={`GAC Pro: change where ${r.name} sits in the boundary`} />}
        </span>
      </div>
      {open ? (
        <div role="row" className="sw-detail" id={id}>
          <span role="cell" aria-colspan={6}>
            <dl className="sw-dl">
              <dt>Resource ID</dt><dd className="sw-mono">{r.resource_key}</dd>
              <dt>First seen</dt><dd>{clockText(r.first_seen_at)}</dd>
              <dt>Last seen</dt><dd>{clockText(r.last_seen_at)}</dd>
            </dl>
          </span>
        </div>
      ) : null}
    </>
  );
}

function DispositionText({ d }: { d: RevealDisposition }) {
  // Plain text for In boundary; chips only for the ones that need a look (round 2 Mediums).
  if (d === 'external') return <span className="sw-chip" data-tone="info">External service</span>;
  if (d === 'unresolved') return <span className="sw-chip" data-tone="warn"><span aria-hidden="true">⚠ </span>Unresolved</span>;
  return <span>{DISPOSITION_TEXT[d]}</span>;
}

function ClueRow({ c, slug, canWrite, open, onOpen, onDone }: {
  c: UnresolvedClue; slug: string; canWrite: boolean; open: boolean; onOpen: (open: boolean) => void; onDone: (text: string, decided: UnresolvedClue) => Promise<void>;
}) {
  const name = c.raw_identifier;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const notOurs = async () => {
    if (busy) return; setBusy(true); setErr(null);
    try { await dismissClue(slug, c.clue_id, 'Not ours'); await onDone(`${name} marked not ours`, c); }
    catch (e) { setErr(e instanceof Error ? e.message : 'It could not be saved.'); } finally { setBusy(false); }
  };
  return (
    <>
      <div role="row" className="sw-tr sw-clue" data-clue={c.clue_id}>
        <span role="cell" className="sw-name"><span className="sw-name-line"><b>{name}</b></span><span className="sw-sub">{c.clue_kind === 'sdk' ? 'A library in your code' : 'An address in your code'}</span></span>
        <span role="cell" data-label="Environment">Environment not known</span>
        <span role="cell" data-label="Account or project">—</span>
        <span role="cell" data-label="Disposition"><DispositionText d="unresolved" /></span>
        <span role="cell" data-label="Found in">{clueWhere(c)}</span>
        <span role="cell" className="sw-act">
          {canWrite ? <>
            <Button compact variant="secondary" ref={confirmRef} data-part="confirm" aria-expanded={open} onClick={() => onOpen(!open)}>Confirm it’s used<span className="sr-only">{`: ${name}`}</span></Button>
            <Button compact variant="ghost" aria-disabled={busy || undefined} onClick={() => void notOurs()}>Not ours<span className="sr-only">{`: ${name}`}</span></Button>
          </> : null}
          {err ? <span className="gp-field-err" role="alert">{err}</span> : null}
        </span>
      </div>
      {open ? (
        <div role="row" className="sw-detail">
          <span role="cell" aria-colspan={6}>
            <ConfirmForm name={name} guess={guessProvider(name)} onCancel={() => { onOpen(false); confirmRef.current?.focus(); }}
              onSave={async (body) => {
                if (body.kind === 'other') {
                  await resolveClueUnlisted(slug, c.clue_id, { name: body.name, website: body.website || null });
                  await onDone(`${body.name} saved as an external service · provider pending`, c);
                  return;
                }
                await resolveClue(slug, c.clue_id, { provider_key: body.provider_key, product: body.product });
                await onDone(`${name} confirmed as ${getProvider(body.provider_key)?.name ?? body.provider_key}`, c);
              }} />
          </span>
        </div>
      ) : null}
    </>
  );
}

/** The provider select's "Other (not listed)…" value (BU-4). */
export const OTHER_PROVIDER = '__other__';

/** BU-4: a provider name and website to start "Other (not listed)…" from what was found (`api.mixpanel.com` → Mixpanel, https://mixpanel.com). */
export function unlistedDefaults(raw: string): { name: string; website: string } {
  const s = raw.trim().toLowerCase();
  const cap = (t: string) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : '');
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(s)) {
    const labels = s.split('.');
    return { name: cap(labels[labels.length - 2] ?? ''), website: `https://${labels.slice(-2).join('.')}` };
  }
  return { name: cap(s.replace(/^(npm|pypi):/, '').replace(/^@/, '').split(/[/:]/)[0] ?? ''), website: '' };
}

type ConfirmBody = { kind: 'catalog'; provider_key: string; product: string } | { kind: 'other'; name: string; website: string };

/**
 * Built-UI (Medium "Clue Confirm"): the provider from GAC Pro's catalog (pre-filled from what was found), then its
 * product. BU-4: "Other (not listed)…" takes a name (and its website): saved as an External service with the provider
 * pending, and GAC Pro is asked to add it — never "Not ours" for something you use.
 */
function ConfirmForm({ name, guess, onCancel, onSave }: { name: string; guess: string; onCancel: () => void; onSave: (b: ConfirmBody) => Promise<void> }) {
  const [provider, setProvider] = useState(guess);
  const [product, setProduct] = useState('*');
  const defaults = useMemo(() => unlistedDefaults(name), [name]);
  const [otherName, setOtherName] = useState(defaults.name);
  const [website, setWebsite] = useState(defaults.website);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formErr, setFormErr] = useState<string | null>(null);
  const first = useRef<HTMLSelectElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const providers = useMemo(() => [...PROVIDERS].sort((a, b) => a.name.localeCompare(b.name)), []);
  const other = provider === OTHER_PROVIDER;
  const products = other ? [] : getProvider(provider)?.products ?? [];
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!provider) errs.provider = `Name the provider ${name} belongs to.`;
    if (other && !otherName.trim()) errs.name = 'Name the provider.';
    if (other && website.trim() && !/^https:\/\/\S+$/i.test(website.trim())) errs.website = 'Use its https:// address, or leave it empty.';
    setErrors(errs);
    if (errs.provider) { first.current?.focus(); return; }
    if (errs.name || errs.website) { nameRef.current?.focus(); return; }
    setFormErr(null);
    try { await onSave(other ? { kind: 'other', name: otherName.trim(), website: website.trim() } : { kind: 'catalog', provider_key: provider, product }); }
    catch (err) { setFormErr(err instanceof Error ? err.message : 'It could not be saved.'); }
  };
  return (
    <form className="sw-form" onSubmit={submit} noValidate aria-label={`Confirm ${name} is used`} data-esc-owner="" onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); onCancel(); } }}>
      {formErr ? <Alert variant="error">{formErr}</Alert> : null}
      <Field id="cf-provider" label="Provider" required error={errors.provider}
        hint={other ? 'Saved as an external service with the provider pending; GAC Pro is asked to add it to the list.' : undefined}>
        <Select ref={first} value={provider} onChange={(e) => { setProvider(e.currentTarget.value); setProduct('*'); }}>
          <option value="">Choose a provider</option>
          {providers.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
          <option value={OTHER_PROVIDER}>Other (not listed)…</option>
        </Select>
      </Field>
      {other ? <>
        <Field id="cf-other-name" label="Provider name" required error={errors.name}>
          <Input ref={nameRef} value={otherName} maxLength={120} autoComplete="off" onChange={(e) => setOtherName(e.currentTarget.value)} />
        </Field>
        <Field id="cf-other-site" label="Website" error={errors.website}>
          <Input type="url" inputMode="url" value={website} maxLength={500} placeholder="https://" autoComplete="off" onChange={(e) => setWebsite(e.currentTarget.value)} />
        </Field>
      </> : null}
      {products.length ? (
        <Field id="cf-product" label="Product">
          <Select value={product} onChange={(e) => setProduct(e.currentTarget.value)}>
            <option value="*">{`Any ${getProvider(provider)?.name ?? ''} product`}</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </Field>
      ) : null}
      <div className="gp-actions"><Button type="submit" variant="secondary">Save</Button><Button variant="ghost" onClick={onCancel}>Cancel</Button></div>
    </form>
  );
}
